import React, { useState } from 'react';
import { cn } from '../../utils/cn';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: 'auto' | 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  delay?: number;
  maxWidth?: string;
  offset?: number;
  minWidth?: string;
}

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

export function Tooltip({
  content,
  children,
  position = 'auto',
  className,
  delay = 200,
  maxWidth = 'clamp(260px, 50vw, 640px)',
  offset = 8,
  minWidth = 'min(90vw, 300px)'
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [computedPos, setComputedPos] = useState<TooltipPosition>('bottom');
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [nudgeX, setNudgeX] = useState(0);
  const [nudgeY, setNudgeY] = useState(0);

  const computePosition = () => {
    if (position !== 'auto') {
      setComputedPos(position as TooltipPosition);
      return;
    }
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) {
      setComputedPos('bottom');
      return;
    }
    const spaceTop = rect.top;
    const spaceBottom = window.innerHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = window.innerWidth - rect.right;

    // Prioriter vertikal plass først (føles mest naturlig)
    if (spaceBottom >= 120) {
      setComputedPos('bottom');
    } else if (spaceTop >= 120) {
      setComputedPos('top');
    } else if (spaceRight >= spaceLeft) {
      setComputedPos('right');
    } else {
      setComputedPos('left');
    }
  };

  const showTooltip = () => {
    const id = window.setTimeout(() => {
      computePosition();
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  // Generer en unik ID basert på innholdet
  const tooltipId = React.useMemo(() => {
    const stringContent = typeof content === 'string' 
      ? content 
      : 'tooltip';
    return `tooltip-${stringContent.toLowerCase().replace(/\s+/g, '-')}`;
  }, [content]);

  // Stil beregnet ut fra valgt posisjon og offset
  const tooltipStyle: React.CSSProperties = React.useMemo(() => {
    const style: React.CSSProperties = { maxWidth, minWidth };
    const px = `${offset}px`;
    let baseTransform = '';
    switch (computedPos) {
      case 'top':
        style.left = '50%';
        style.bottom = `calc(100% + ${px})`;
        baseTransform = 'translateX(-50%)';
        break;
      case 'bottom':
        style.left = '50%';
        style.top = `calc(100% + ${px})`;
        baseTransform = 'translateX(-50%)';
        break;
      case 'right':
        style.left = `calc(100% + ${px})`;
        style.top = '50%';
        baseTransform = 'translateY(-50%)';
        break;
      case 'left':
        style.right = `calc(100% + ${px})`;
        style.top = '50%';
        baseTransform = 'translateY(-50%)';
        break;
    }
    // Legg til dynamiske justeringer for å hindre overflow
    if (computedPos === 'top' || computedPos === 'bottom') {
      style.transform = `${baseTransform} translateX(${-nudgeX}px)`;
    } else {
      style.transform = `${baseTransform} translateY(${-nudgeY}px)`;
    }
    return style;
  }, [computedPos, offset, maxWidth, minWidth, nudgeX, nudgeY]);

  // Juster posisjon slik at tooltip ikke går utenfor skjermen
  const adjustWithinViewport = React.useCallback(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    // Horisontal justering når top/bottom
    if (computedPos === 'top' || computedPos === 'bottom') {
      const overflowRight = Math.max(0, rect.right - (window.innerWidth - margin));
      const overflowLeft = Math.max(0, margin - rect.left);
      const net = overflowRight - overflowLeft;
      setNudgeX(net);
      setNudgeY(0);
    } else {
      // Vertikal justering når venstre/høyre
      const overflowBottom = Math.max(0, rect.bottom - (window.innerHeight - margin));
      const overflowTop = Math.max(0, margin - rect.top);
      const net = overflowBottom - overflowTop;
      setNudgeY(net);
      setNudgeX(0);
    }
  }, [computedPos]);

  React.useEffect(() => {
    if (!isVisible) return;
    const raf = requestAnimationFrame(adjustWithinViewport);
    const onResize = () => adjustWithinViewport();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isVisible, adjustWithinViewport]);

  return (
    <div
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      <div
        role="tooltip"
        id={tooltipId}
        ref={tooltipRef}
        className={cn(
          'absolute z-50 px-3 py-2 text-sm leading-5 text-white bg-gray-900 rounded shadow-lg break-words',
          'pointer-events-none opacity-0 transition-opacity duration-200',
          isVisible && 'opacity-100',
          className
        )}
        style={tooltipStyle}
        aria-hidden={!isVisible}
      >
        {content}
      </div>
      <div
        aria-describedby={tooltipId}
      >
        {children}
      </div>
    </div>
  );
}